import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  MAX_CSMS_SECONDARIES,
  WS_URL_REGEX,
  type ExternalCsmsFormState,
  type SecondaryCsmsFormEndpoint,
} from '~/app/components/organization/organizationExternalCsms.utils';

type UseOrganizationCsmsArgs = {
  value: ExternalCsmsFormState;
  savedSecondaryCount: number;
  onChange: (next: ExternalCsmsFormState) => void;
  onRemoveSecondary: (index: number) => void;
};

export function useOrganizationCsms({
  value,
  savedSecondaryCount,
  onChange,
  onRemoveSecondary,
}: UseOrganizationCsmsArgs) {
  const { t } = useTranslation('organization');
  const secondaries = value.secondaries ?? [];
  const primaryLocked = savedSecondaryCount > 0 || secondaries.length > 0;
  const secondaryLimitReached = secondaries.length >= MAX_CSMS_SECONDARIES;

  const patch = useCallback(
    (partial: Partial<ExternalCsmsFormState>) => {
      onChange({ ...value, ...partial });
    },
    [onChange, value],
  );

  const patchSecondary = useCallback(
    (index: number, partial: Partial<SecondaryCsmsFormEndpoint>) => {
      patch({
        secondaries: secondaries.map((endpoint, endpointIndex) =>
          endpointIndex === index ? { ...endpoint, ...partial } : endpoint,
        ),
      });
    },
    [patch, secondaries],
  );

  const urlError = useCallback(
    (baseUrl: string) =>
      baseUrl.trim().length > 0 && !WS_URL_REGEX.test(baseUrl.trim())
        ? t('dialog.csmsUrlInvalid')
        : undefined,
    [t],
  );

  const changeEnableCsms = useCallback(
    (checked: boolean) => {
      patch({
        enableCsms: checked,
        chargerAuthenticationEnabled: checked
          ? value.chargerAuthenticationEnabled
          : false,
      });
    },
    [patch, value.chargerAuthenticationEnabled],
  );

  const changePrimaryBaseUrl = useCallback(
    (baseUrl: string) => patch({ csmsBaseUrl: baseUrl }),
    [patch],
  );

  const changePrimarySubprotocol = useCallback(
    (subprotocol: ExternalCsmsFormState['csmsSubprotocol']) =>
      patch({ csmsSubprotocol: subprotocol }),
    [patch],
  );

  const changeChargerAuthentication = useCallback(
    (enabled: boolean) => patch({ chargerAuthenticationEnabled: enabled }),
    [patch],
  );

  const addSecondary = useCallback(() => {
    if (secondaryLimitReached) {
      return;
    }
    patch({
      secondaries: [
        ...secondaries,
        {
          enabled: true,
          baseUrl: '',
          subprotocol: value.csmsSubprotocol,
          sortOrder: secondaries.length + 1,
        },
      ],
    });
  }, [patch, secondaries, secondaryLimitReached, value.csmsSubprotocol]);

  const changeSecondaryBaseUrl = useCallback(
    (index: number, baseUrl: string) => patchSecondary(index, { baseUrl }),
    [patchSecondary],
  );

  const changeSecondarySubprotocol = useCallback(
    (index: number, subprotocol: SecondaryCsmsFormEndpoint['subprotocol']) =>
      patchSecondary(index, { subprotocol }),
    [patchSecondary],
  );

  const changeSecondaryEnabled = useCallback(
    (index: number, enabled: boolean) => patchSecondary(index, { enabled }),
    [patchSecondary],
  );

  return {
    secondaries,
    primaryLocked,
    secondaryLimitReached,
    urlError,
    changeEnableCsms,
    changePrimaryBaseUrl,
    changePrimarySubprotocol,
    changeChargerAuthentication,
    addSecondary,
    changeSecondaryBaseUrl,
    changeSecondarySubprotocol,
    changeSecondaryEnabled,
    removeSecondary: onRemoveSecondary,
  };
}
